import { KEY } from '@constants/key';
import { handleApiResult } from '@store/error';
import { removeAuth, setAuth } from '../reducers/authSlice';
import { apiSlice } from './apiSlice';
import { type User } from './userEndpoints';

interface LoginResponse {
  tokens: {
    accessToken: string;
    refreshToken: string;
  }
  user: User;
}

type RefreshTokenResponse = LoginResponse['tokens'];

interface LoginRequest {
  username: string;
  password: string;
}

interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/v1/auth',
        method: 'POST',
        body: credentials,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          localStorage.setItem(KEY.ACCESS_TOKEN, data.tokens.accessToken);
          localStorage.setItem(KEY.REFRESH_TOKEN, data.tokens.refreshToken);
          dispatch(setAuth(data.user));
        } catch (_) { /* empty */ }
      },
    }),
    refreshToken: builder.mutation<RefreshTokenResponse, { refreshToken: string }>({
      query: ({ refreshToken }) => ({
        url: '/v1/auth/refresh-token',
        method: 'POST',
        body: { refreshToken },
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/auth',
        method: 'DELETE',
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          localStorage.removeItem(KEY.ACCESS_TOKEN);
          localStorage.removeItem(KEY.REFRESH_TOKEN);
          dispatch(removeAuth());
          dispatch(apiSlice.util.resetApiState());
        } catch (_) { /* empty */ }
      },
    }),
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (passwords) => ({
        url: '/v1/auth/password',
        method: 'POST',
        body: passwords,
      }),
      onQueryStarted: handleApiResult('Api_Auth_Password_Change_Successful', { handle401Error: true }),
    }),
    getSession: builder.query<LoginResponse['user'], void>({
      query: () => '/v1/auth',
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAuth(data));
        } catch (_) {
          localStorage.removeItem(KEY.ACCESS_TOKEN);
          localStorage.removeItem(KEY.REFRESH_TOKEN);
          dispatch(removeAuth());
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useGetSessionQuery,
} = authApiSlice;
